// SUB-OFFICE 7 — collision. A 0.25m occupancy grid baked from walls + prop
// footprints; capsule resolve as two axis passes (slide, never stop dead).
// Yellow-line and DO-NOT-STAND cells are flagged so telemetry is one array
// lookup, not geometry math. Zero allocation after bake.
//
// v2 (M-A): the grid is now a SWAPPABLE ACTIVE STATE, not a single module
// singleton. The resident interior floor keeps its grid cached; a streamed /
// boot-only zone (the Arrival Exterior) installs its own grid on entry and
// restores the interior grid on exit — exactly the "swap the active grid
// pointer on transition" model in spec-map-v2 tech_architecture. The exported
// hot-path surface (slideCapsule / zoneAt / collisionStats / CELL) is
// unchanged; only bakeCollision() gained two siblings.

import {
  WALLS, SOLIDS, ZONES, WORLD_BOUNDS, WALL_T,
  type WallSeg, type SolidRect, type ZoneRect,
} from './layout';

export const CELL = 0.25;

export interface CollisionBounds { x0: number; z0: number; x1: number; z1: number }

/** Everything a zone's occupancy grid is baked from. */
export interface CollisionPayload {
  walls: readonly WallSeg[];
  solids: readonly SolidRect[];
  zones: readonly ZoneRect[];
  bounds: CollisionBounds;
  wallT?: number;
}

// bit 0: solid; bit 1: yellow line; bit 2: do-not-stand
interface GridState {
  grid: Uint8Array;
  nx: number;
  nz: number;
  x0: number;
  z0: number;
}

function buildGridState(p: CollisionPayload): GridState {
  const nx = Math.ceil((p.bounds.x1 - p.bounds.x0) / CELL);
  const nz = Math.ceil((p.bounds.z1 - p.bounds.z0) / CELL);
  const grid = new Uint8Array(nx * nz);
  const s: GridState = { grid, nx, nz, x0: p.bounds.x0, z0: p.bounds.z0 };
  const half = (p.wallT ?? WALL_T) / 2;
  for (const w of p.walls) markRectInto(s, w.x0 - half, w.z0 - half, w.x1 + half, w.z1 + half, 1);
  for (const so of p.solids) markRectInto(s, so.x0, so.z0, so.x1, so.z1, 1);
  for (const z of p.zones) markRectInto(s, z.x0, z.z0, z.x1, z.z1, z.kind === 'yellow' ? 2 : 4);
  // world border: everything outside the perimeter is solid by construction
  return s;
}

function interiorPayload(): CollisionPayload {
  return { walls: WALLS, solids: SOLIDS, zones: ZONES, bounds: WORLD_BOUNDS, wallT: WALL_T };
}

// The resident interior grid stays cached so restore is a pointer swap, never
// a rebuild. `active` is whatever grid the player currently walks.
let interiorState: GridState = buildGridState(interiorPayload());
let active: GridState = interiorState;

function idx(s: GridState, cx: number, cz: number): number {
  return cz * s.nx + cx;
}
function toCellX(s: GridState, x: number): number {
  return Math.floor((x - s.x0) / CELL);
}
function toCellZ(s: GridState, z: number): number {
  return Math.floor((z - s.z0) / CELL);
}

function markRectInto(s: GridState, x0: number, z0: number, x1: number, z1: number, bit: number): void {
  const cx0 = Math.max(0, toCellX(s, Math.min(x0, x1)));
  const cx1 = Math.min(s.nx - 1, toCellX(s, Math.max(x0, x1)));
  const cz0 = Math.max(0, toCellZ(s, Math.min(z0, z1)));
  const cz1 = Math.min(s.nz - 1, toCellZ(s, Math.max(z0, z1)));
  for (let cz = cz0; cz <= cz1; cz++) {
    for (let cx = cx0; cx <= cx1; cx++) s.grid[idx(s, cx, cz)] |= bit;
  }
}

/** Bake the resident interior floor into the active grid. ~1ms. Boot calls this. */
export function bakeCollision(): void {
  interiorState = buildGridState(interiorPayload());
  active = interiorState;
}

/** Install a zone's own grid as active (the Arrival Exterior on entry). */
export function useZoneCollision(payload: CollisionPayload): void {
  active = buildGridState(payload);
}

/** Restore the resident interior grid (crossing the threshold back inside). */
export function restoreInteriorCollision(): void {
  active = interiorState;
}

function solidAt(x: number, z: number): boolean {
  const cx = toCellX(active, x);
  const cz = toCellZ(active, z);
  if (cx < 0 || cz < 0 || cx >= active.nx || cz >= active.nz) return true;
  return (active.grid[idx(active, cx, cz)] & 1) !== 0;
}

/** Capsule-vs-grid: does a circle of radius r at (x,z) overlap any solid? */
function circleBlocked(x: number, z: number, r: number): boolean {
  // sample the 4 cardinal + 4 diagonal extremes plus center — cheap and stable
  if (solidAt(x, z)) return true;
  if (solidAt(x + r, z) || solidAt(x - r, z) || solidAt(x, z + r) || solidAt(x, z - r)) return true;
  const d = r * 0.7071;
  return solidAt(x + d, z + d) || solidAt(x - d, z + d) || solidAt(x + d, z - d) || solidAt(x - d, z - d);
}

export interface SlideResult {
  x: number;
  z: number;
  hitX: boolean;
  hitZ: boolean;
}

const slideOut: SlideResult = { x: 0, z: 0, hitX: false, hitZ: false };

/**
 * Move a capsule (radius r) from (x,z) by (dx,dz), sliding along walls:
 * axis-separated resolve — X pass then Z pass. Returns a reused object;
 * consume immediately (hot path, no allocation).
 */
export function slideCapsule(x: number, z: number, dx: number, dz: number, r: number): SlideResult {
  let nx = x + dx;
  if (circleBlocked(nx, z, r)) {
    // step back in sub-cell increments to hug the wall
    const step = Math.sign(dx) * CELL * 0.25;
    nx = x;
    let tries = 8;
    while (tries-- > 0 && !circleBlocked(nx + step, z, r) && Math.abs(nx + step - x) <= Math.abs(dx)) nx += step;
    slideOut.hitX = true;
  } else {
    slideOut.hitX = false;
  }
  let nz = z + dz;
  if (circleBlocked(nx, nz, r)) {
    const step = Math.sign(dz) * CELL * 0.25;
    nz = z;
    let tries = 8;
    while (tries-- > 0 && !circleBlocked(nx, nz + step, r) && Math.abs(nz + step - z) <= Math.abs(dz)) nz += step;
    slideOut.hitZ = true;
  } else {
    slideOut.hitZ = false;
  }
  slideOut.x = nx;
  slideOut.z = nz;
  return slideOut;
}

/** Zone flags at a point: 0 none, 2 yellow line, 4 do-not-stand (bitmask). */
export function zoneAt(x: number, z: number): number {
  const cx = toCellX(active, x);
  const cz = toCellZ(active, z);
  if (cx < 0 || cz < 0 || cx >= active.nx || cz >= active.nz) return 0;
  return active.grid[idx(active, cx, cz)] & 6;
}

/** Debug: count solid cells (sanity in smoke logs). */
export function collisionStats(): { cells: number; solid: number } {
  let solid = 0;
  for (let i = 0; i < active.grid.length; i++) if (active.grid[i] & 1) solid++;
  return { cells: active.grid.length, solid };
}
